import { toast } from 'react-hot-toast';
import type { AxiosRequestConfig } from 'axios';
import type { ScopedClient } from '@/session/scopedTransport';
import { cleanChannelName, toSnakeCase } from './common';

const BASE_FIELDS: string[] = [
  'id',
  'group',
  'sku',
  'name',
  'ean',
  'gtin',
  'isbn',
  'mpn',
  'upc',
  'pricing_item.price.amount',
  'pricing_item.price.currency',
  'pricing_item.price.presentation',
  'pricing_item.msrp.presentation',
  'pricing_item.msrp.amount',
  'pricing_item.msrp.currency',
  'pricing_item.mapp.amount',
  'pricing_item.mapp.currency',
  'pricing_item.mapp.presentation',
];

/**
 * Fetches catalog field names from the API and updates component state via the provided setter.
 * Mirrors the legacy behaviour so existing screens keep working while re-using new utilities below.
 */
export const buildFieldNames = (data: unknown): string[] => {
  if (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    typeof (data as { status?: unknown }).status === 'number' &&
    (data as { status: number }).status >= 400
  ) {
    throw new Error('Error getting field names');
  }

  const fieldNames = [...BASE_FIELDS];

  if (typeof data === 'object' && data !== null && 'result' in data && Array.isArray((data as { result?: unknown }).result)) {
    (data as { result: Array<{ name: string; valid_unit_values?: string[] }> }).result.forEach((field) => {
      if (field?.valid_unit_values && field.valid_unit_values.length > 0) {
        fieldNames.push(`fields.${field.name}.value`);
        fieldNames.push(`fields.${field.name}.unit`);
      } else {
        fieldNames.push(`fields.${field.name}`);
      }
    });
  }

  return fieldNames;
};

export type FieldNamesRequestOptions = Pick<AxiosRequestConfig, 'signal' | 'operationRequirement'>;
export type FieldNamesClient = Pick<ScopedClient, 'get'>;

export const getFieldNamesOrThrow = async (client: FieldNamesClient, limit = 1000, options: FieldNamesRequestOptions = {}): Promise<string[]> => {
  const response = await client.get<unknown>('catalog/fields', {
    ...options,
    params: { limit },
  });
  return buildFieldNames(response.data);
};

export const getFieldNames = async (
  client: FieldNamesClient,
  setFieldNames: (names: string[]) => void,
  limit = 1000,
  options: FieldNamesRequestOptions = {}
): Promise<void> => {
  try {
    const fieldNames = await getFieldNamesOrThrow(client, limit, options);
    setFieldNames(fieldNames);
  } catch (error: any) {
    toast.error(error?.message ?? 'Failed to get field names');
    setFieldNames([]);
  }
};

/**
 * Utility for getting field names from objects
 */
export const extractFieldNames = <T extends Record<string, unknown>>(obj: T): (keyof T)[] => {
  if (!obj || typeof obj !== 'object') {
    return [];
  }
  return Object.keys(obj) as (keyof T)[];
};

/**
 * Sanitizes field names by handling camelCase conversion and special characters
 */
export const sanitizeFieldName = (name: string) => {
  return (
    name
      .trim()
      // Handle camelCase conversion first
      .replace(/([a-z\d])([A-Z])/g, '$1_$2') // Insert underscore between lowercase/digit and uppercase
      .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2') // Handle consecutive capitals
      .toLowerCase()
      .replace(/[^a-zA-Z0-9 _]/g, ' ') // Replace special characters with spaces (preserve existing underscores)
      .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
      .trim() // Remove leading/trailing spaces
      .replace(/ /g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .replace(/^_+|_+$/g, '')
  ); // Remove leading/trailing underscores
};

/**
 * Normalizes user-facing "suggested field name" strings.
 *
 * This is intentionally a light wrapper so multiple screens can share consistent
 * suggestion formatting rules in one place.
 *
 * Current behavior:
 * - Converts to snake_case via sanitizeFieldName
 */
export const normalizeSuggestedFieldName = (name: string): string => {
  return sanitizeFieldName(name);
};

/**
 * Drops the `fields.` namespace from a field path so the create endpoint gets a
 * bare name.
 *
 * The prefix is recognized after trimming and without regard to case — `Fields.`
 * and a padded ` fields.` name the same namespace — but the remainder keeps its
 * original casing, because `sanitizeFieldName` still has camelCase to split.
 * Matching the prefix literally instead let it survive into the created name.
 */
export const stripFieldsPrefix = (name: string): string => {
  const raw = typeof name === 'string' ? name : '';
  const trimmed = raw.trim();
  return trimmed.toLowerCase().startsWith('fields.') ? trimmed.slice('fields.'.length) : trimmed;
};

export const addFieldsPrefix = (name: string): string => {
  const raw = typeof name === 'string' ? name : '';
  return raw.startsWith('fields.') ? raw : `fields.${raw}`;
};

export const getFieldNameVariants = (name: string): [string, string] => {
  const raw = typeof name === 'string' ? name.trim() : '';
  const withoutPrefix = stripFieldsPrefix(raw).trim();
  const withPrefix = addFieldsPrefix(withoutPrefix).trim();
  return [withoutPrefix, withPrefix];
};

/**
 * Generates a complete field name by combining cleaned channel name with sanitized field name
 */
export const generateFieldName = (channelName: string, fieldName: string) => {
  const actualFieldName = stripFieldsPrefix(fieldName);

  // Format as <channel_name>_<field_name_snake_case>
  const cleanedChannelName = cleanChannelName(channelName);
  const channelSnakeCase = toSnakeCase(cleanedChannelName);
  const fieldSnakeCase = sanitizeFieldName(actualFieldName); // Use sanitizeFieldName for field names with special characters
  return `${channelSnakeCase}_${fieldSnakeCase}`;
};
